/**
 * Centralized multilingual Telegram notification templates.
 *
 * - The notification language is the agency preference `telegramLang`
 *   ('fr' | 'en' | 'ar'), read from saved settings. Default: 'fr'.
 * - Only text labels/messages are translated. All data values
 *   (vehicle name, plate, amounts, dates, km, speed, etc.) are passed
 *   through unchanged.
 * - To add a NEW notification type, just add a builder to BUILDERS below;
 *   it will automatically support the selected language everywhere.
 *
 * Usage:
 *   import { buildTelegramMessage } from '../../../utils/telegramMessages';
 *   sendTelegramNotification(buildTelegramMessage('new_rental', { ... }), 'new_rental');
 */

const SUPPORTED = ['fr', 'en', 'ar'];

/** Resolve the notification language (optional explicit override → settings → 'fr'). */
export const resolveNotifLang = (explicit) => {
  if (SUPPORTED.includes(explicit)) return explicit;
  try {
    const s = JSON.parse(localStorage.getItem('appSettings') || '{}');
    if (SUPPORTED.includes(s.telegramLang)) return s.telegramLang;
  } catch (_) {}
  return 'fr';
};

/** Pick a localized string; falls back to fr if a language is missing. */
const pick = (L, obj) => obj[L] ?? obj.fr;

const dash = '—';

// Maintenance type labels (fr/en/ar) — keyed by the stored maintenance key.
const MAINT_TYPE_LABELS = {
  'Vidange': { fr: "Vidange d'huile", en: 'Oil change', ar: 'تغيير الزيت' },
  'Filtre à huile': { fr: 'Filtre à huile', en: 'Oil filter', ar: 'فلتر الزيت' },
  'Filtre à air': { fr: 'Filtre à air', en: 'Air filter', ar: 'فلتر الهواء' },
  'Filtre à carburant': { fr: 'Filtre à carburant', en: 'Fuel filter', ar: 'فلتر الوقود' },
  'Courroie de distribution': { fr: 'Courroie de distribution', en: 'Timing belt', ar: 'حزام التوقيت' },
  'Pneus': { fr: 'Pneus', en: 'Tires', ar: 'الإطارات' },
  'Freins': { fr: 'Freins', en: 'Brakes', ar: 'الفرامل' },
  'Bougies': { fr: 'Bougies', en: 'Spark plugs', ar: 'الشمعات' },
  'Batterie': { fr: 'Batterie', en: 'Battery', ar: 'البطارية' },
  'Climatisation': { fr: 'Climatisation', en: 'Air conditioning', ar: 'التكييف' },
  'Autre': { fr: 'Autre', en: 'Other', ar: 'أخرى' },
};
const maintTypeLabel = (key, L) => (MAINT_TYPE_LABELS[key] ? pick(L, MAINT_TYPE_LABELS[key]) : (key || dash));

// Staff payment type labels.
const STAFF_PAY_LABELS = {
  salary: { fr: 'Salaire', en: 'Salary', ar: 'راتب', emoji: '💰' },
  advance: { fr: 'Avance', en: 'Advance', ar: 'سلفة', emoji: '⏩' },
  bonus: { fr: 'Prime', en: 'Bonus', ar: 'علاوة', emoji: '🎁' },
  penalty: { fr: 'Pénalité', en: 'Penalty', ar: 'خصم', emoji: '⛔' },
};

const v = (x) => (x === 0 ? '0' : (x || dash)); // keep 0, replace null/undefined/'' with dash

const BUILDERS = {
  // ───────── Contracts & Rentals ─────────
  new_rental: (d, L) => {
    const open = pick(L, { fr: 'Contrat ouvert ⏳', en: 'Open contract ⏳', ar: 'عقد مفتوح ⏳' });
    const end = d.isOpen ? open : (d.endDate || dash);
    return (
      `🚗 <b>${pick(L, { fr: 'Nouvelle location', en: 'New rental', ar: 'إيجار جديد' })}</b>\n` +
      `👤 ${v(d.clientName)}\n` +
      `🚙 ${v(d.carName)}\n` +
      `📅 ${v(d.startDate)} → ${end}\n` +
      `💰 ${v(d.amount)} ${d.currency || ''}`
    );
  },

  rental_completed: (d, L) => {
    const closed = d.isOpenClosed
      ? ' ' + pick(L, { fr: '(Contrat ouvert clôturé ⏳)', en: '(Open contract closed ⏳)', ar: '(تم إغلاق العقد المفتوح ⏳)' })
      : '';
    const dayWord = pick(L, { fr: 'jour(s)', en: 'day(s)', ar: 'يوم' });
    const creditLine = pick(L, { fr: 'Crédit ajouté au client', en: 'Credit added to client', ar: 'تمت إضافة دين على العميل' });
    return (
      `✅ <b>${pick(L, { fr: 'Location terminée', en: 'Rental completed', ar: 'انتهى الإيجار' })}${closed}</b>\n` +
      `${v(d.clientName)} - ${v(d.carName)}\n` +
      `📊 ${v(d.days)} ${dayWord} - ${v(d.total)} ${d.currency || ''}\n` +
      `💰 <i>${creditLine}: ${v(d.creditAdded)} ${d.currency || ''}</i>`
    );
  },

  contract_signed: (d, L) => {
    const openLbl = pick(L, { fr: 'Ouvert', en: 'Open', ar: 'مفتوح' });
    const end = d.isOpen ? openLbl : (d.endDate || dash);
    return (
      `✍️ <b>${pick(L, { fr: 'Contrat Signé', en: 'Contract Signed', ar: 'تم توقيع العقد' })}</b>\n` +
      `👤 ${pick(L, { fr: 'Client', en: 'Client', ar: 'العميل' })}: <b>${v(d.clientName)}</b>\n` +
      `🚗 ${pick(L, { fr: 'Voiture', en: 'Vehicle', ar: 'السيارة' })}: <b>${v(d.carName)}</b>\n` +
      `💰 ${pick(L, { fr: 'Montant', en: 'Amount', ar: 'المبلغ' })}: <b>${v(d.amount)} ${d.currency || ''}</b>\n` +
      `📅 ${pick(L, { fr: 'Période', en: 'Period', ar: 'الفترة' })}: <b>${v(d.startDate)} → ${end}</b>\n` +
      `⏰ ${v(d.time)}`
    );
  },

  new_reservation: (d, L) => (
    `✅ <b>${pick(L, { fr: 'Réservation confirmée', en: 'Reservation confirmed', ar: 'تم تأكيد الحجز' })}</b>\n` +
    `👤 ${v(d.clientName)}\n` +
    `🚗 ${v(d.carName)}\n` +
    `📅 ${v(d.startDate)} → ${v(d.endDate)}`
  ),

  online_reservation: (d, L) => (
    `🎉 <b>${pick(L, { fr: 'Nouvelle Réservation Online', en: 'New Online Reservation', ar: 'حجز جديد عبر الإنترنت' })}</b>\n` +
    `🚗 ${v(d.carName)}${d.carPlate ? ` (${d.carPlate})` : ''}\n` +
    `👤 ${v(d.name)}\n` +
    `📞 ${v(d.phone)}\n` +
    `🆔 ${v(d.cin)}\n` +
    `📅 ${v(d.startDate)} → ${v(d.endDate)}\n` +
    `⏱️ ${v(d.totalDays)} ${pick(L, { fr: 'jour(s)', en: 'day(s)', ar: 'يوم' })}\n` +
    `💰 ${v(d.totalPrice)} ${d.currency || ''}` +
    (d.notes ? `\n📝 ${d.notes}` : '')
  ),

  // ───────── Vehicle Alerts ─────────
  new_car: (d, L) => (
    `🚗 <b>${pick(L, { fr: 'Nouvelle voiture', en: 'New vehicle', ar: 'سيارة جديدة' })}</b>\n` +
    `${v(d.brand)} ${v(d.model)}\n` +
    `🔖 ${v(d.plate)}`
  ),

  engine_control: (d, L) => (
    `🔧 <b>${pick(L, { fr: 'Pompe Essence', en: 'Fuel Pump', ar: 'مضخة الوقود' })}</b>\n` +
    `🚗 ${v(d.brand)} ${v(d.model)} (${v(d.plate)})\n` +
    `⚡ ${pick(L, { fr: 'Action', en: 'Action', ar: 'الإجراء' })}: <b>${v(d.action)}</b>\n` +
    `📡 ${pick(L, { fr: 'Commande', en: 'Command', ar: 'الأمر' })}: <code>${v(d.command)}</code>\n` +
    `⏰ ${v(d.time)}`
  ),

  // ───────── Maintenance & Service ─────────
  vidange_done: (d, L) => {
    const km = pick(L, { fr: 'km', en: 'km', ar: 'كم' });
    const next = pick(L, { fr: 'Prochain', en: 'Next', ar: 'القادم' });
    const cur = typeof d.currentKm === 'number' ? d.currentKm.toLocaleString() : v(d.currentKm);
    const nxt = typeof d.nextKm === 'number' ? d.nextKm.toLocaleString() : v(d.nextKm);
    return (
      `🔧 <b>${pick(L, { fr: 'Nouvel Entretien', en: 'New Service', ar: 'صيانة جديدة' })}</b>\n` +
      `🚗 ${v(d.carName)}\n` +
      `📌 ${pick(L, { fr: 'Type', en: 'Type', ar: 'النوع' })}: ${maintTypeLabel(d.typeKey, L)}\n` +
      `📍 ${cur} ${km} (${next}: ${nxt} ${km})\n` +
      `💰 ${pick(L, { fr: 'Coût', en: 'Cost', ar: 'التكلفة' })}: ${v(d.cost)} ${d.currency || ''}`
    );
  },

  _maint_alert: (d, L) => {
    const statusMap = {
      overdue: pick(L, { fr: '🔴 En retard', en: '🔴 Overdue', ar: '🔴 متأخر جداً' }),
      critical: pick(L, { fr: '🟠 Critique', en: '🟠 Critical', ar: '🟠 حرج' }),
      warning: pick(L, { fr: '🟡 Proche', en: '🟡 Approaching', ar: '🟡 قريب' }),
    };
    const km = pick(L, { fr: 'km', en: 'km', ar: 'كم' });
    const cur = typeof d.currentKm === 'number' ? Math.round(d.currentKm).toLocaleString() : v(d.currentKm);
    const due = typeof d.nextKm === 'number' ? Math.round(d.nextKm).toLocaleString() : v(d.nextKm);
    return (
      `🔧 <b>${pick(L, { fr: 'Alerte Entretien', en: 'Maintenance Alert', ar: 'تنبيه الصيانة' })} : ${maintTypeLabel(d.typeKey, L)}</b>\n` +
      `🚗 ${v(d.carName)} (${v(d.plate)})\n` +
      `📊 ${pick(L, { fr: 'Statut', en: 'Status', ar: 'الحالة' })} : ${statusMap[d.level] || v(d.level)}\n` +
      `📍 ${pick(L, { fr: 'Actuel', en: 'Current', ar: 'الكيلومترات الحالية' })} : ${cur} ${km}\n` +
      `⚙️ ${pick(L, { fr: 'Échéance', en: 'Due', ar: 'حد الصيانة' })} : ${due} ${km}`
    );
  },

  // ───────── System Notifications ─────────
  new_client: (d, L) => (
    `🆕 <b>${pick(L, { fr: 'Nouveau client', en: 'New client', ar: 'عميل جديد' })}</b>\n\n` +
    `👤 <b>${pick(L, { fr: 'Nom complet', en: 'Full name', ar: 'الاسم الكامل' })}:</b> ${v(d.name)}\n` +
    `📞 <b>${pick(L, { fr: 'Téléphone', en: 'Phone', ar: 'الهاتف' })}:</b> ${v(d.phone)}\n` +
    `🪪 <b>CIN:</b> ${v(d.cin)}\n` +
    `🚗 <b>${pick(L, { fr: 'N° Permis', en: 'License N°', ar: 'رقم الرخصة' })}:</b> ${v(d.permis)}\n` +
    `📧 <b>Email:</b> ${v(d.email)}\n` +
    `🏠 <b>${pick(L, { fr: 'Adresse', en: 'Address', ar: 'العنوان' })}:</b> ${v(d.address)}\n` +
    `🌍 <b>${pick(L, { fr: 'Nationalité', en: 'Nationality', ar: 'الجنسية' })}:</b> ${v(d.nationality)}\n` +
    `📅 <b>${pick(L, { fr: 'Date de naissance', en: 'Date of birth', ar: 'تاريخ الازدياد' })}:</b> ${v(d.birthDate)}\n` +
    `📅 <b>${pick(L, { fr: 'Date délivrance permis', en: 'License issue date', ar: 'تاريخ تسليم الرخصة' })}:</b> ${v(d.permisDate)}\n` +
    `💼 <b>${pick(L, { fr: 'Profession', en: 'Profession', ar: 'المهنة' })}:</b> ${v(d.profession)}\n` +
    `☎️ <b>${pick(L, { fr: 'Téléphone fixe', en: 'Landline', ar: 'الهاتف الثابت' })}:</b> ${v(d.phone2)}\n` +
    `📍 <b>${pick(L, { fr: 'Autre adresse', en: 'Other address', ar: 'عنوان آخر' })}:</b> ${v(d.address2)}\n` +
    `📝 <b>Notes:</b> ${v(d.notes)}`
  ),

  client_payment: (d, L) => (
    `💰 <b>${pick(L, { fr: 'Paiement Reçu', en: 'Payment Received', ar: 'تم استلام دفعة' })}</b>\n` +
    `👤 ${pick(L, { fr: 'Client', en: 'Client', ar: 'العميل' })}: <b>${v(d.clientName)}</b>\n` +
    `💵 ${pick(L, { fr: 'Montant', en: 'Amount', ar: 'المبلغ' })}: <b>${v(d.amount)} ${d.currency || ''}</b>\n` +
    `💳 ${pick(L, { fr: 'Mode', en: 'Method', ar: 'الطريقة' })}: ${v(d.method)}\n` +
    `📝 ${pick(L, { fr: 'Note', en: 'Note', ar: 'ملاحظة' })}: ${d.note || '-'}\n` +
    `📊 ${pick(L, { fr: 'Nouveau Solde', en: 'New Balance', ar: 'الرصيد الجديد' })}: <b>${v(d.balance)} ${d.currency || ''}</b>`
  ),

  new_staff: (d, L) => {
    const period = d.salaryType === 'monthly'
      ? pick(L, { fr: 'mensuel', en: 'monthly', ar: 'شهري' })
      : pick(L, { fr: 'journalier', en: 'daily', ar: 'يومي' });
    return (
      `👤 <b>${pick(L, { fr: 'Nouvel employé', en: 'New employee', ar: 'موظف جديد' })}</b>\n` +
      `📋 ${pick(L, { fr: 'Nom', en: 'Name', ar: 'الاسم' })}: ${v(d.name)}\n` +
      `💼 ${pick(L, { fr: 'Poste', en: 'Position', ar: 'المنصب' })}: ${v(d.role)}\n` +
      `💰 ${pick(L, { fr: 'Salaire', en: 'Salary', ar: 'الراتب' })}: ${v(d.salaryAmount)} ${d.currency || ''} (${period})`
    );
  },

  staff_payment: (d, L) => {
    const lbl = STAFF_PAY_LABELS[d.typeKey] || { emoji: '💼', fr: d.typeKey, en: d.typeKey, ar: d.typeKey };
    const sign = d.typeKey === 'penalty' ? '-' : '+';
    return (
      `${lbl.emoji} <b>${pick(L, lbl)} - ${v(d.name)}</b>\n` +
      `💼 ${pick(L, { fr: 'Poste', en: 'Position', ar: 'المنصب' })}: ${v(d.role)}\n` +
      `💰 ${pick(L, { fr: 'Montant', en: 'Amount', ar: 'المبلغ' })}: ${sign}${v(d.amount)} ${d.currency || ''}\n` +
      `📅 ${pick(L, { fr: 'Date', en: 'Date', ar: 'التاريخ' })}: ${v(d.date)}` +
      (d.period ? `\n📆 ${pick(L, { fr: 'Période', en: 'Period', ar: 'الفترة' })}: ${d.period}` : '') +
      (d.note ? `\n📝 ${d.note}` : '')
    );
  },

  new_cheque: (d, L) => {
    const title = d.type === 'received'
      ? pick(L, { fr: 'Nouveau chèque reçu', en: 'New cheque received', ar: 'شيك جديد مستلم' })
      : pick(L, { fr: 'Nouveau chèque émis', en: 'New cheque issued', ar: 'شيك جديد صادر' });
    return (
      `💵 <b>${title}</b>\n` +
      `🔢 N°: ${v(d.number)}\n` +
      `💰 ${v(d.amount)} ${d.currency || ''}\n` +
      `🏦 ${d.bank || '-'}\n` +
      `👤 ${d.holder || '-'}\n` +
      `📅 ${pick(L, { fr: 'Échéance', en: 'Due date', ar: 'تاريخ الاستحقاق' })}: ${v(d.dueDate)}`
    );
  },

  cheque_status: (d, L) => {
    const statusMap = {
      cashed: pick(L, { fr: '✅ Encaissé', en: '✅ Cashed', ar: '✅ تم الصرف' }),
      bounced: pick(L, { fr: '⚠️ Rejeté', en: '⚠️ Bounced', ar: '⚠️ مرفوض' }),
      cancelled: pick(L, { fr: '❌ Annulé', en: '❌ Cancelled', ar: '❌ ملغى' }),
    };
    return (
      `💵 <b>${pick(L, { fr: 'Chèque mis à jour', en: 'Cheque updated', ar: 'تم تحديث الشيك' })}</b>\n` +
      `🔢 N°: ${v(d.number)}\n` +
      `💰 ${v(d.amount)} ${d.currency || ''}\n` +
      `📊 ${statusMap[d.status] || v(d.status)}`
    );
  },

  treatment_unpaid: (d, L) => (
    `⚠️ <b>${pick(L, { fr: 'Traitement impayé / en retard', en: 'Treatment unpaid / overdue', ar: 'معالجة غير مدفوعة / متأخرة' })}</b>\n` +
    (d.label ? `📋 ${d.label}\n` : '') +
    (d.clientName ? `👤 ${v(d.clientName)}\n` : '') +
    `💰 ${pick(L, { fr: 'Montant', en: 'Amount', ar: 'المبلغ' })}: <b>${v(d.amount)} ${d.currency || ''}</b>` +
    (d.dueDate ? `\n📅 ${pick(L, { fr: 'Échéance', en: 'Due date', ar: 'تاريخ الاستحقاق' })}: ${d.dueDate}` : '')
  ),

  treatment_paid: (d, L) => (
    `✅ <b>${pick(L, { fr: 'Traitement payé', en: 'Treatment paid', ar: 'معالجة مدفوعة' })}</b>\n` +
    (d.label ? `📋 ${d.label}\n` : '') +
    (d.clientName ? `👤 ${v(d.clientName)}\n` : '') +
    `💰 ${pick(L, { fr: 'Montant', en: 'Amount', ar: 'المبلغ' })}: <b>${v(d.amount)} ${d.currency || ''}</b>` +
    (d.date ? `\n📅 ${pick(L, { fr: 'Date', en: 'Date', ar: 'التاريخ' })}: ${d.date}` : '')
  ),

  contravention: (d, L) => (
    `⚠️ <b>${pick(L, { fr: 'Nouvelle Contravention NARSA', en: 'New NARSA Fine', ar: 'مخالفة نارسا جديدة' })}</b>\n` +
    `🚗 ${v(d.carName)}${d.carPlate ? ` (${d.carPlate})` : ''}\n` +
    `👤 ${v(d.clientName)} ${d.clientFirstName || ''}\n` +
    `📅 ${d.date || '-'}\n` +
    `🔢 ${pick(L, { fr: 'Avis N°', en: 'Notice N°', ar: 'رقم الإشعار' })}: ${d.avisNumber || '-'}` +
    (d.amount ? `\n💰 ${d.amount} ${d.currency || ''}` : '')
  ),

  // ───────── Speed Alerts ─────────
  speed_alert: (d, L) => (
    `🚨 ${pick(L, { fr: 'Alerte Vitesse', en: 'Speed Alert', ar: 'تنبيه السرعة' })}!\n` +
    `🚗 ${pick(L, { fr: 'Véhicule', en: 'Vehicle', ar: 'المركبة' })}: ${v(d.carLabel)}\n` +
    `💨 ${pick(L, { fr: 'Vitesse actuelle', en: 'Current speed', ar: 'السرعة الحالية' })}: ${v(d.speed)} kph\n` +
    `⚠️ ${pick(L, { fr: 'Limite configurée', en: 'Configured limit', ar: 'الحد المسموح' })}: ${v(d.limit)} kph\n` +
    `🕒 ${v(d.time)}`
  ),

  // ───────── Hood / Capot (DI1/DI2) ─────────
  hood_opened: (d, L) => (
    `🚨 ${pick(L, { fr: 'Capot ouvert (Hood Opened)', en: 'Hood Opened', ar: 'فتح غطاء المحرك (الكابو)' })}!\n` +
    `🚗 ${pick(L, { fr: 'Véhicule', en: 'Vehicle', ar: 'المركبة' })}: ${v(d.carLabel)}\n` +
    `⚠️ ${pick(L, { fr: 'Événement', en: 'Event', ar: 'الحدث' })}: ${pick(L, { fr: 'Capot ouvert', en: 'Hood opened', ar: 'الكابو مفتوح' })}\n` +
    `🕒 ${v(d.time)}`
  ),

  // ───────── Ignition / Moteur (ACC) ─────────
  ignition: (d, L) => (
    `${d.on ? '🟢' : '🔴'} ${d.on
      ? pick(L, { fr: 'Moteur démarré', en: 'Engine started', ar: 'تشغيل المحرك' })
      : pick(L, { fr: 'Moteur arrêté', en: 'Engine stopped', ar: 'إيقاف المحرك' })}!\n` +
    `🚗 ${pick(L, { fr: 'Véhicule', en: 'Vehicle', ar: 'المركبة' })}: ${v(d.carLabel)}\n` +
    `🕒 ${v(d.time)}`
  ),

  // ───────── Breakdown / Panne ─────────
  breakdown_reported: (d, L) => (
    `🛠️ ${pick(L, { fr: 'PANNE SIGNALÉE', en: 'BREAKDOWN REPORTED', ar: 'تم الإبلاغ عن عطل' })}!\n` +
    `🚗 ${pick(L, { fr: 'Véhicule', en: 'Vehicle', ar: 'المركبة' })}: ${v(d.carLabel)}\n` +
    (d.severity ? `🔺 ${pick(L, { fr: 'Gravité', en: 'Severity', ar: 'الخطورة' })}: ${v(d.severity)}\n` : '') +
    (d.comment ? `📝 ${pick(L, { fr: 'Description', en: 'Description', ar: 'الوصف' })}: ${v(d.comment)}\n` : '') +
    (d.address ? `📍 ${v(d.address)}\n` : '') +
    (d.mapsUrl ? `🗺️ ${v(d.mapsUrl)}\n` : '') +
    `🔧 ${pick(L, { fr: 'Statut', en: 'Status', ar: 'الحالة' })}: ${pick(L, { fr: 'En réparation', en: 'Under repair', ar: 'قيد الإصلاح' })}\n` +
    `🕒 ${v(d.time)}`
  ),
  breakdown_closed: (d, L) => (
    `✅ ${pick(L, { fr: 'RÉPARATION TERMINÉE', en: 'REPAIR CLOSED', ar: 'انتهى الإصلاح' })}\n` +
    `🚗 ${pick(L, { fr: 'Véhicule', en: 'Vehicle', ar: 'المركبة' })}: ${v(d.carLabel)}\n` +
    (d.duration ? `⏱️ ${pick(L, { fr: 'Durée de réparation', en: 'Repair duration', ar: 'مدة الإصلاح' })}: ${v(d.duration)}\n` : '') +
    (d.comment ? `📝 ${v(d.comment)}\n` : '') +
    `🕒 ${v(d.time)}`
  ),

  // ───────── Blacklist approval workflow ─────────
  blacklist_approved: (d, L) => (
    `✅ ${pick(L, { fr: 'Demande liste noire APPROUVÉE', en: 'Blacklist request APPROVED', ar: 'تمت الموافقة على طلب القائمة السوداء' })}\n` +
    `👤 ${v(d.name)}\n` +
    (d.approver ? `👮 ${pick(L, { fr: 'Approuvé par', en: 'Approved by', ar: 'وافق عليه' })}: ${v(d.approver)}\n` : '') +
    `🕒 ${v(d.time)}`
  ),
  blacklist_rejected: (d, L) => (
    `❌ ${pick(L, { fr: 'Demande liste noire REJETÉE', en: 'Blacklist request REJECTED', ar: 'تم رفض طلب القائمة السوداء' })}\n` +
    `👤 ${v(d.name)}\n` +
    (d.reason ? `📝 ${pick(L, { fr: 'Motif', en: 'Reason', ar: 'السبب' })}: ${v(d.reason)}\n` : '') +
    `🕒 ${v(d.time)}`
  ),
  blacklist_moreinfo: (d, L) => (
    `ℹ️ ${pick(L, { fr: 'Informations requises (liste noire)', en: 'More info required (blacklist)', ar: 'مطلوب معلومات إضافية' })}\n` +
    `👤 ${v(d.name)}\n` +
    (d.message ? `📝 ${v(d.message)}\n` : '') +
    `🕒 ${v(d.time)}`
  ),

  // ───────── Geofencing ─────────
  geo_change: (d, L) => (
    `🏙️ ${pick(L, { fr: 'Changement de ville', en: 'City change', ar: 'تغيير المدينة' })}!\n` +
    `🚗 ${pick(L, { fr: 'Véhicule', en: 'Vehicle', ar: 'المركبة' })}: ${v(d.carLabel)}\n` +
    `📍 ${pick(L, { fr: 'Ville précédente', en: 'Previous city', ar: 'المدينة السابقة' })}: ${v(d.prevCity)}\n` +
    `🆕 ${pick(L, { fr: 'Nouvelle ville', en: 'New city', ar: 'المدينة الجديدة' })}: ${v(d.newCity)}\n` +
    `🕒 ${v(d.time)}`
  ),

  geo_enter: (d, L) => (
    `🏙️ ${pick(L, { fr: 'Entrée en ville', en: 'City entry', ar: 'دخول مدينة جديدة' })}!\n` +
    `🚗 ${pick(L, { fr: 'Véhicule', en: 'Vehicle', ar: 'المركبة' })}: ${v(d.carLabel)}\n` +
    `📍 ${pick(L, { fr: 'Ville', en: 'City', ar: 'المدينة' })}: ${v(d.newCity)}\n` +
    `🕒 ${v(d.time)}`
  ),

  geo_leave: (d, L) => (
    `🏙️ ${pick(L, { fr: 'Quitter une ville', en: 'City exit', ar: 'مغادرة مدينة' })}!\n` +
    `🚗 ${pick(L, { fr: 'Véhicule', en: 'Vehicle', ar: 'المركبة' })}: ${v(d.carLabel)}\n` +
    `📤 ${pick(L, { fr: 'Ville quittée', en: 'City left', ar: 'المدينة المغادرة' })}: ${v(d.prevCity)}\n` +
    `🕒 ${v(d.time)}`
  ),
};

// Map the maintenance per-type alert keys to the shared builder.
BUILDERS.maint_warning = BUILDERS._maint_alert;
BUILDERS.maint_critical = BUILDERS._maint_alert;
BUILDERS.maint_overdue = BUILDERS._maint_alert;

// Client payment from history uses the same template.
BUILDERS.client_history_payment = BUILDERS.client_payment;

/**
 * Build a localized Telegram message.
 * @param {string} templateKey - which template to render (e.g. 'speed_alert').
 * @param {object} data - data values (passed through unchanged).
 * @param {string} [langOverride] - force a language ('fr'|'en'|'ar').
 * @returns {string}
 */
export const buildTelegramMessage = (templateKey, data = {}, langOverride) => {
  const L = resolveNotifLang(langOverride);
  const fn = BUILDERS[templateKey];
  if (!fn) return data._fallback || '';
  return fn(data, L);
};
